import { getResult } from "../../services/FetchServices";
import ProtectedLink from "../containers/auth/ProtectedLink";
import { useAuth } from "../providers/AuthProvider";

const TRANSPORTATION_ROLES = [
  "ROLE_VendorFulfillmentTransportationPlanner",
  "ROLE_VendorAdministrator",
  "ROLE_VendorFulfillmentOperator",
];

const PAYMENT_ROLES = [
  "ROLE_VendorFinanceUser",
  "ROLE_VendorFinanceMerchAdmin",
];

const MERCH_ADMIN_ROLES = ["ROLE_VendorFinanceMerchAdmin"];
const MERCH_FINANCE_ROLES = ["ROLE_VendorFinanceUser"];

function Navbar() {
  const { roles = undefined, showSupplierPayments, isLoading } = useAuth();

  const handleLogout = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/logout`;
    getResult(url);
  };

  const hasAccess = (allowedRoles) => {
    if (!allowedRoles || allowedRoles.length === 0) return true;
    return allowedRoles.some((role) => roles?.includes(role));
  };

  if (isLoading) return null;

  const isTransportationUser = hasAccess(TRANSPORTATION_ROLES);
  const isMerchFinanceUser = hasAccess(MERCH_FINANCE_ROLES);
  const isMerchFinanceAdmin = hasAccess(MERCH_ADMIN_ROLES);

  const LocationsDropdown = ({ checkRoles = true }) => (
    <li className="nav-item dropdown px-lg-4">
      <a
        className="nav-link dropdown-toggle text-uppercase"
        id="navbarDropdownLocations"
        href="/#"
        role="button"
        data-bs-toggle="dropdown"
        aria-expanded="false"
      >
        Locations
      </a>
      <ul
        className="dropdown-menu dropdown-menu-end"
        aria-labelledby="navbarDropdownLocations"
      >
        <li>
          <ProtectedLink
            to="/newLocation"
            allowedRoles={checkRoles ? TRANSPORTATION_ROLES : undefined}
            className="dropdown-item"
            id="newLocation"
          >
            Create Location
          </ProtectedLink>
        </li>
        <li>
          <ProtectedLink
            to="/findLocation"
            allowedRoles={checkRoles ? TRANSPORTATION_ROLES : undefined}
            className="dropdown-item"
            id="findOrEditLocation"
          >
            Search/Edit Location
          </ProtectedLink>
        </li>
      </ul>
    </li>
  );

  const SRRDropdown = ({ checkRoles = true }) => (
    <li className="nav-item dropdown px-lg-4">
      <a
        className="nav-link dropdown-toggle text-uppercase"
        id="navbarDropdownOrders"
        href="/#"
        role="button"
        data-bs-toggle="dropdown"
        aria-expanded="false"
      >
        Supplier Routing Request
      </a>
      <ul
        className="dropdown-menu dropdown-menu-end"
        aria-labelledby="navbarDropdownOrders"
      >
        <li>
          <ProtectedLink
            to="/searchPurchaseOrderRelease"
            allowedRoles={checkRoles ? TRANSPORTATION_ROLES : undefined}
            className="dropdown-item"
            id="searchPurchaseOrderRelease"
          >
            Create Supplier Routing Request From Purchase Order
          </ProtectedLink>
        </li>
        <li>
          <ProtectedLink
            to="/searchOrderRelease"
            allowedRoles={checkRoles ? TRANSPORTATION_ROLES : undefined}
            className="dropdown-item"
            id="searchOrderRelease"
          >
            Search Existing Supplier Routing Request
          </ProtectedLink>
        </li>
      </ul>
    </li>
  );

  const PaymentsDropdown = ({ checkRoles = true }) => (
    <li className="nav-item dropdown px-lg-4">
      <a
        className="nav-link dropdown-toggle text-uppercase"
        id="navbarDropdownPayments"
        href="/#"
        role="button"
        data-bs-toggle="dropdown"
        aria-expanded="false"
      >
        Supplier Payments
      </a>
      <ul
        className="dropdown-menu dropdown-menu-end"
        aria-labelledby="navbarDropdownPayments"
      >
        <li>
          <ProtectedLink
            to="/invoicePaymentStatus"
            className="dropdown-item"
            id="invoicePaymentStatus"
            allowedRoles={checkRoles ? PAYMENT_ROLES : undefined}
          >
            Payment Status
          </ProtectedLink>
        </li>
      </ul>
    </li>
  );

  const renderContactUsEmail = () => {
    if (isMerchFinanceAdmin || isMerchFinanceUser) {
      return (
        <a href="mailto:vendorpartners@dollartree.com">
          vendorpartners@dollartree.com
        </a>
      );
    }
    if (isTransportationUser) {
      return (
        <a href="mailto:vendorlogin@dollartree.com">
          vendorlogin@dollartree.com
        </a>
      );
    }
    return null;
  };

  const ContactUsDropdown = () => (
    <li className="nav-item dropdown px-lg-4">
      <a
        className="nav-link dropdown-toggle text-uppercase"
        id="navbarDropdownContactUs"
        href="/#"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        role="button"
      >
        Contact Us
      </a>
      <ul
        className="dropdown-menu dropdown-menu-end"
        aria-labelledby="navbarDropdownContactUs"
      >
        <li>
          <address className="dropdown-item mb-0">
            {renderContactUsEmail()}
          </address>
        </li>
      </ul>
    </li>
  );

  const renderNavLinks = () => {
    if (!showSupplierPayments) {
      return isTransportationUser ? (
        <>
          <LocationsDropdown checkRoles={false} />
          <SRRDropdown checkRoles={false} />
        </>
      ) : null;
    }

    if (isMerchFinanceAdmin || isMerchFinanceUser) {
      return hasAccess(PAYMENT_ROLES) ? (
        <PaymentsDropdown checkRoles={true} />
      ) : null;
    }

    if (isTransportationUser) {
      return (
        <>
          <LocationsDropdown checkRoles={true} />
          <SRRDropdown checkRoles={true} />
        </>
      );
    }

    return null;
  };

  return (
    <nav className="navbar navbar-expand-lg navbar-dark py-lg-4" id="mainNav">
      <div className="container">
        <ProtectedLink
          to="/home"
          className="navbar-brand text-uppercase fw-bold d-lg-none"
        >
          CVP
        </ProtectedLink>
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarSupportedContent"
          aria-controls="navbarSupportedContent"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className="collapse navbar-collapse" id="navbarSupportedContent">
          <ul className="navbar-nav mx-auto">
            {/* HOME */}
            <li className="nav-item px-lg-4">
              <ProtectedLink to="/home" className="nav-link text-uppercase">
                HOME
              </ProtectedLink>
            </li>

            {renderNavLinks()}

            {/* FAQ */}
            <li className="nav-item px-lg-4">
              <ProtectedLink
                to="/faq"
                className="nav-link text-uppercase"
                id="faq"
                role="button"
              >
                FAQ
              </ProtectedLink>
            </li>

            {/* CONTACT US */}
            <ContactUsDropdown />
          </ul>
        </div>
      </div>
      <div>
        <button
          id="logoutButton"
          type="button"
          className="btn btn-success"
          style={{
            backgroundColor: "red",
            marginRight: "5px",
            color: "white",
            fontWeight: "800",
          }}
          onClick={handleLogout}
        >
          Logout
        </button>
      </div>
    </nav>
  );
}

export default Navbar;
